import BaseView from "./base-view";
import ModelLoader from "../model-loader";
import assets from "../assets";
import getLogger from "../logging";
import hitPlaneMaterial from "../view3d/hit-plane-material";
import pc from "../parametric-cache";
import { Box3, BufferGeometry, Material, Mesh, Object3D, PlaneGeometry } from "three";
import { FENCE_INSET, getSlantedShelfHeight, getSlantedShelfRakeHeight, SHELF_ANGLE } from "../controllers/slanted-shelf-controllers";
import { SlantedShelf } from "../domain";

const
    ml = ModelLoader.getInstance(),
    log = getLogger('views');


export class SlantedShelfView extends BaseView {

    declare model: SlantedShelf;

    shelf: Mesh;
    fence: Object3D;
    hit: Mesh;
    hitBox: Box3 = new Box3();

    constructor(model: SlantedShelf) {
        super(model);

        this.init();
    }

    make() {
        this.name = 'SlantedShelfView';

        this.shelf = new Mesh(new BufferGeometry(), new Material());
        this.shelf.castShadow = this.shelf.receiveShadow = true;
        this.add(this.shelf);

        this.shelf.userData.isSelectable = true;
        this.shelf.userData.isMovable = true;
        this.shelf.userData.cursor = 'row-resize';
        this.shelf.userData.isParametricPart = true;

        this.shelf.rotation.set(SHELF_ANGLE, 0, 0);

        this.highlights.add(this.shelf);

        this.hit = new Mesh(new PlaneGeometry(), hitPlaneMaterial);
        this.hit.geometry.translate(0.5, 0.5, 0);
        this.hit.name = 'SlantedShelf hit rectangle';
        this.hit.visible = false;
        this.hit.userData.isSelectable = true;
        this.hit.userData.isMovable = true;
        this.add(this.hit);
    }

    bind() {
        let changeSize = this.changeSize.bind(this);
        this.model.bind('width', changeSize);
        this.model.on('thickness', changeSize);
        this.model.on('depth', changeSize);
    
        this.bindMaterialArray(this.shelf, this.model.shelfMaterials);
        this.model.bind('opacity', () => {
            const castShadow = this.model.opacity == 1;
            this.shelf.castShadow = castShadow;
        });
    }

    changeSize() {
        this.shelf.position.set(0, getSlantedShelfRakeHeight(this.model), 0);
        this.hit.scale.set(
            this.model.width,
            getSlantedShelfHeight(this.model),
            1);
        this.updates.queue(
            async () => {
                const geoPromise = pc.make('BasicEdgeBandedShelf',
                                    this.model.width,
                                    this.model.thickness,
                                    this.model.depth);

                let fencePromise: Promise<Object3D | void>;
                if(this.model.fenceAssetId) {
                    const url = assets.resolve(
                        this.model.fenceAssetId, 'LowResModel');
                    fencePromise = ml.get(url).catch(log.error);
                } else {
                    fencePromise = Promise.resolve();
                }
                return await Promise.all( [geoPromise, fencePromise]);
            },
            ([geo, fence]) => {
                this.shelf.geometry = geo;
                if(fence) {
                    this.fence?.removeFromParent();
                    this.fence = fence;
                    this.fence.position.set(
                        0,
                        this.model.thickness,
                        this.model.depth - FENCE_INSET);
                    this.fence.scale.set(this.model.width/23, 1, 1);
                    this.bindMaterialTree(this.fence, this.model.fenceMaterials);

                    this.shelf.add(fence);
                }
                this.onReady.trigger();
            });
    }
}
export default SlantedShelfView;
